import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Pencil, Image as ImageIcon, Film, X, Check, Volume2, VolumeX } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAdmin } from "@/contexts/AdminContext";
import { useSettings } from "@/contexts/SettingsContext";
import { readCache, writeCache, isMeaningfulResponse } from "@/lib/persistCache";

/**
 * Homepage Category Billboards (5 slots).
 *
 * UX rules (v25b — non-obstructive edit):
 *   • Visitors see ONLY image/video + title (and subtitle if set). No icons,
 *     no pills, no labels, no controls obstructing the image.
 *   • Admins see tool buttons (upload, pencil) only — small, top-right.
 *     Clicking the pencil opens a single inline editor with title + subtitle
 *     fields, so no permanent "SUBTITLE" / "AUTOPLAY" pills clutter the card.
 *   • Featured Hub video: a small speaker icon at the bottom-right edge lets
 *     ANYONE (admin or visitor) toggle mute. Tap the video itself to play /
 *     pause when autoplay is off. No "FEATURED" pill, no "AUTOPLAY ON" pill.
 *
 * Slot order (locked):
 *   1. Architectural Facade
 *   2. Modular Systems
 *   3. Pergolas & Shades
 *   4. Design + Build
 *   5. Featured Hub
 */
/**
 * Slot order (locked). Note: we do NOT use Unsplash mockups as fallbacks
 * anymore — they were rendering for ~1 second on every page load before the
 * /api/categories response came in, making it look like the admin's images
 * had been overwritten. The local cache (below) now hydrates the cards
 * instantly with the admin's real images.
 */
const SLOT_ORDER = [
  { slug: "architectural-facade", name: "Architectural Facade" },
  { slug: "modular-systems",     name: "Modular Systems" },
  { slug: "pergolas-shades",     name: "Pergolas & Shades" },
  { slug: "design-build",        name: "Design + Build", subtitle: "Remodeling + Renovation" },
];

export default function CategoryToggle() {
  // Hydrate INSTANTLY from cached categories. No more Unsplash-mockup flash
  // while the API call is pending. After the first successful load we keep
  // the cache fresh; if the API ever returns empty/garbage during a backend
  // hiccup, we keep the cached snapshot.
  const [cats, setCats] = useState(() => readCache("categories") || []);
  const { editing } = useAdmin();
  const { settings, refresh: refreshSettings } = useSettings();

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      if (isMeaningfulResponse(data)) {
        setCats(Array.isArray(data) ? data : []);
        writeCache("categories", data);
      }
    } catch {
      // network error during pod cold-start — keep cached snapshot
    }
  };

  useEffect(() => { load(); }, []);

  const orderedSlots = SLOT_ORDER.map((spec) => {
    const dbRow = cats.find((c) => c.slug === spec.slug);
    return {
      ...spec,
      cat: dbRow || null,
      name: dbRow?.name || spec.name,
      subtitle: dbRow?.subtitle || spec.subtitle || "",
      image_url: dbRow?.image_url || null,
    };
  });

  return (
    <div className="mx-auto max-w-[935px] px-3 md:px-10 pt-4 md:pt-6 pb-2" data-testid="category-toggle">
      <div className="grid grid-cols-3 gap-2 md:gap-3">
        {orderedSlots.slice(0, 3).map((s) => (
          <BillboardCard
            key={s.slug}
            cat={s.cat}
            label={s.name}
            slug={s.slug}
            imageUrl={s.image_url}
            subtitle={s.subtitle}
            editable={editing}
            onEdited={load}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 md:gap-3 mt-2 md:mt-3">
        <BillboardCard
          cat={orderedSlots[3].cat}
          label={orderedSlots[3].name}
          slug={orderedSlots[3].slug}
          imageUrl={orderedSlots[3].image_url}
          subtitle={orderedSlots[3].subtitle}
          editable={editing}
          onEdited={load}
        />
        <FeaturedHubCard
          settings={settings}
          editable={editing}
          onSaved={refreshSettings}
        />
      </div>
    </div>
  );
}

/**
 * Static billboard card — visitor clicks do nothing.
 * Admin: upload icon (top-left) and pencil icon (top-right) only. The
 * pencil opens an inline 2-field editor (title + subtitle) so we don't
 * need a separate "SUBTITLE" button taking up real estate on the image.
 */
function BillboardCard({ cat, label, slug, imageUrl, subtitle, editable, onEdited }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingOpen, setEditingOpen] = useState(false);
  const [draftName, setDraftName] = useState(label);
  const [draftSub, setDraftSub] = useState(subtitle);

  useEffect(() => { setDraftName(label); }, [label]);
  useEffect(() => { setDraftSub(subtitle); }, [subtitle]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !cat) {
      if (!cat) toast.error("This category doesn't exist in the database yet — open Admin → Categories to create it.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await api.patch(`/categories/${cat.id}`, { image_url: data.url });
      toast.success("Image updated");
      onEdited?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!cat) { setEditingOpen(false); return; }
    const changes = {};
    if (draftName !== label) changes.name = draftName;
    if ((draftSub || "") !== (subtitle || "")) changes.subtitle = draftSub;
    if (Object.keys(changes).length === 0) { setEditingOpen(false); return; }
    try {
      await api.patch(`/categories/${cat.id}`, changes);
      toast.success("Saved");
      onEdited?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEditingOpen(false); }
  };

  return (
    <div
      className="relative overflow-hidden rounded-md border border-[color:var(--bda-border)] aspect-[5/4] hover:border-[color:var(--bda-blue-glow)]/40 transition-colors"
      data-testid={`category-card-${slug}`}
    >
      {imageUrl ? (
        <img
          src={imageUrl.startsWith("http") ? imageUrl : fileUrl(imageUrl)}
          alt={label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/15 pointer-events-none" />
      <div className="absolute inset-0 flex items-end p-2.5 md:p-4 pointer-events-none">
        <div className="text-white">
          <div className="font-heading text-sm md:text-lg font-medium leading-tight" data-testid={`category-title-${slug}`}>
            {label}
          </div>
          {subtitle && (
            <div className="text-[10px] md:text-[11px] mt-0.5 text-white/85 leading-tight font-light" data-testid={`category-subtitle-${slug}`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Admin chrome — small, top corners only. Invisible to visitors. */}
      {editable && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white border border-white/15 z-10"
            title="Replace image"
            data-testid={`category-card-${slug}-replace`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => setEditingOpen(true)}
            className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white border border-white/15 z-10"
            title="Edit title and subtitle"
            data-testid={`category-card-${slug}-edit`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />

          {editingOpen && (
            <TwoFieldEditor
              titleValue={draftName}
              subtitleValue={draftSub || ""}
              onTitleChange={setDraftName}
              onSubtitleChange={setDraftSub}
              onSave={save}
              onCancel={() => { setEditingOpen(false); setDraftName(label); setDraftSub(subtitle || ""); }}
              testIdPrefix={`category-card-${slug}`}
            />
          )}
        </>
      )}
    </div>
  );
}

function TwoFieldEditor({ titleValue, subtitleValue, onTitleChange, onSubtitleChange, onSave, onCancel, testIdPrefix }) {
  return (
    <div
      className="absolute inset-0 z-30 bg-black/85 backdrop-blur-sm flex flex-col gap-1.5 p-2.5 justify-center"
      onClick={(e) => e.stopPropagation()}
      data-testid={`${testIdPrefix}-editor`}
    >
      <input
        autoFocus
        value={titleValue}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Title"
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        className="px-2 py-1.5 text-xs rounded bg-white text-black placeholder:text-slate-400"
        data-testid={`${testIdPrefix}-editor-title`}
      />
      <input
        value={subtitleValue}
        onChange={(e) => onSubtitleChange(e.target.value)}
        placeholder="Subtitle (optional)"
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        className="px-2 py-1.5 text-xs rounded bg-white text-black placeholder:text-slate-400"
        data-testid={`${testIdPrefix}-editor-subtitle`}
      />
      <div className="flex gap-1.5 justify-end mt-1">
        <button type="button" onClick={onCancel} className="w-7 h-7 rounded bg-slate-700 text-white flex items-center justify-center" data-testid={`${testIdPrefix}-editor-cancel`}>
          <X className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={onSave} className="w-7 h-7 rounded bg-emerald-600 text-white flex items-center justify-center" data-testid={`${testIdPrefix}-editor-save`}>
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Slot 5 — FEATURED HUB.
 * Visitor: clean image OR autoplay video. The ONLY visible control is a
 * small speaker icon at the bottom-right corner (mute / unmute). Tapping
 * the video itself toggles play/pause. Nothing else obstructs the image.
 *
 * Admin: image-upload, video-upload, and pencil icons (top corners only).
 * The pencil opens a 3-field editor: title, subtitle, autoplay toggle.
 */
function FeaturedHubCard({ settings, editable, onSaved }) {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingOpen, setEditingOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const url = settings?.featured_hub_url;
  const type = settings?.featured_hub_type || "image";
  const poster = settings?.featured_hub_poster;
  const autoplay = settings?.featured_hub_autoplay !== false;
  const title = settings?.featured_hub_title || "Featured Hub";
  const subtitle = settings?.featured_hub_subtitle || "";
  const fallback = settings?.all_category_image
    || "https://images.unsplash.com/photo-1769780510442-60d4d6391a91?auto=format&fit=crop&w=600&q=80";

  const [draftTitle, setDraftTitle] = useState(title);
  const [draftSub, setDraftSub] = useState(subtitle);
  const [draftAutoplay, setDraftAutoplay] = useState(autoplay);
  useEffect(() => { setDraftTitle(title); }, [title]);
  useEffect(() => { setDraftSub(subtitle); }, [subtitle]);
  useEffect(() => { setDraftAutoplay(autoplay); }, [autoplay]);

  const showVideo = type === "video" && url;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const isVideo = data.media_type === "video";
      await api.patch("/settings", {
        featured_hub_url: data.url,
        featured_hub_type: isVideo ? "video" : "image",
        featured_hub_poster: isVideo ? (data.poster_url || null) : null,
      });
      toast.success(isVideo ? "Featured video updated" : "Featured image updated");
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload failed: ${err.message || "network error"}`);
    } finally { setUploading(false); setPickerMode(null); }
  };

  const save = async () => {
    const changes = {};
    if (draftTitle !== title) changes.featured_hub_title = draftTitle;
    if (draftSub !== subtitle) changes.featured_hub_subtitle = draftSub;
    if (draftAutoplay !== autoplay) changes.featured_hub_autoplay = draftAutoplay;
    if (Object.keys(changes).length === 0) { setEditingOpen(false); return; }
    try {
      await api.patch("/settings", changes);
      toast.success("Saved");
      await onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEditingOpen(false); }
  };

  const triggerPick = (mode) => {
    setPickerMode(mode);
    setTimeout(() => fileRef.current?.click(), 0);
  };

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // When unmuting, also make sure the video is actually playing
    // (browsers may keep it paused if autoplay was blocked).
    if (!v.muted && v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    }
  };

  const enableSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    }
  };

  const togglePlay = (e) => {
    // Tap on the video body toggles play/pause. The mute toggle has its
    // own button so it doesn't trigger here (button stops propagation).
    if (!videoRef.current) return;
    const v = videoRef.current;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="relative overflow-hidden rounded-md border border-[color:var(--bda-blue-glow)]/40 aspect-[5/4] bg-black" data-testid="featured-hub">
      {showVideo ? (
        <video
          ref={videoRef}
          src={fileUrl(url)}
          poster={poster ? fileUrl(poster) : undefined}
          autoPlay={autoplay}
          muted={muted}
          loop
          playsInline
          preload="auto"
          controls={false}
          controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
          disablePictureInPicture
          onClick={togglePlay}
          onContextMenu={(e) => e.preventDefault()}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="featured-hub-video"
        />
      ) : (
        <img
          src={url ? (url.startsWith("http") ? url : fileUrl(url)) : fallback}
          alt={title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 pointer-events-none" />

      <div className="absolute inset-0 flex items-end p-2.5 md:p-4 pointer-events-none">
        <div className="text-white">
          <div className="font-heading text-sm md:text-lg font-medium leading-tight" data-testid="featured-hub-title">
            {title}
          </div>
          {subtitle && (
            <div className="text-[10px] md:text-[11px] mt-0.5 text-white/85 leading-tight font-light" data-testid="featured-hub-subtitle">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Visitor-facing mute toggle — small, bottom-right corner, never
          obstructs the image. Only visible when a video is showing. */}
      {showVideo && (
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="featured-hub-mute-toggle"
          className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur flex items-center justify-center text-white border border-white/15 z-20"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
      )}

      {/* Prominent glowing "Tap for sound" badge — visible only while the
          video is muted. Disappears the moment the visitor taps it (or the
          small corner mute icon). Browsers block unmuted autoplay on first
          page load, so this is the user-initiated gesture that turns audio
          on. The badge pulses gently to attract attention without being
          obnoxious. */}
      {showVideo && muted && (
        <button
          type="button"
          onClick={enableSound}
          aria-label="Tap for sound"
          data-testid="featured-hub-tap-for-sound"
          className="absolute left-1/2 -translate-x-1/2 bottom-10 md:bottom-12 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/25 text-white text-[10px] md:text-xs font-medium tracking-wide uppercase animate-pulse shadow-[0_0_18px_rgba(56,189,248,0.7),0_0_36px_rgba(56,189,248,0.35)] hover:shadow-[0_0_24px_rgba(56,189,248,0.9),0_0_48px_rgba(56,189,248,0.5)] transition-shadow"
        >
          <Volume2 className="w-3.5 h-3.5" />
          <span>Tap for sound</span>
        </button>
      )}

      {/* Admin chrome — only visible in edit mode */}
      {editable && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept={pickerMode === "video" ? "video/*" : pickerMode === "image" ? "image/*" : "image/*,video/*"}
            onChange={onPick}
            data-testid="featured-hub-file-input"
            className="hidden"
          />
          {/* TOP-LEFT: image upload */}
          <button
            type="button"
            onClick={() => triggerPick("image")}
            className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white border border-white/15 z-10"
            title="Upload image"
            data-testid="featured-hub-upload-image"
          >
            {uploading && pickerMode === "image" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          </button>
          {/* TOP-LEFT (next to image): video upload */}
          <button
            type="button"
            onClick={() => triggerPick("video")}
            className="absolute top-1.5 left-10 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white border border-white/15 z-10"
            title="Upload video"
            data-testid="featured-hub-upload-video"
          >
            {uploading && pickerMode === "video" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
          </button>
          {/* TOP-RIGHT: pencil → opens combined title/subtitle/autoplay editor */}
          <button
            type="button"
            onClick={() => setEditingOpen(true)}
            className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white border border-white/15 z-10"
            title="Edit title, subtitle, autoplay"
            data-testid="featured-hub-edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>

          {uploading && (
            <div className="absolute inset-0 bg-black/55 backdrop-blur-sm flex flex-col items-center justify-center text-white z-30 pointer-events-none">
              <Loader2 className="w-6 h-6 animate-spin mb-1.5" />
              <span className="text-[9px] tracking-[0.3em] uppercase">Uploading…</span>
            </div>
          )}

          {editingOpen && (
            <FeaturedHubEditor
              titleValue={draftTitle}
              subtitleValue={draftSub}
              autoplayValue={draftAutoplay}
              onTitleChange={setDraftTitle}
              onSubtitleChange={setDraftSub}
              onAutoplayChange={setDraftAutoplay}
              onSave={save}
              onCancel={() => { setEditingOpen(false); setDraftTitle(title); setDraftSub(subtitle); setDraftAutoplay(autoplay); }}
              showAutoplay={showVideo}
            />
          )}
        </>
      )}
      {!playing && showVideo && !editable && null /* visitor-facing play overlay intentionally omitted — non-obstructive */}
    </div>
  );
}

function FeaturedHubEditor({ titleValue, subtitleValue, autoplayValue, onTitleChange, onSubtitleChange, onAutoplayChange, onSave, onCancel, showAutoplay }) {
  return (
    <div
      className="absolute inset-0 z-30 bg-black/85 backdrop-blur-sm flex flex-col gap-1.5 p-2.5 justify-center"
      onClick={(e) => e.stopPropagation()}
      data-testid="featured-hub-editor"
    >
      <input
        autoFocus
        value={titleValue}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Title"
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        className="px-2 py-1.5 text-xs rounded bg-white text-black placeholder:text-slate-400"
        data-testid="featured-hub-editor-title"
      />
      <input
        value={subtitleValue}
        onChange={(e) => onSubtitleChange(e.target.value)}
        placeholder="Subtitle (optional)"
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        className="px-2 py-1.5 text-xs rounded bg-white text-black placeholder:text-slate-400"
        data-testid="featured-hub-editor-subtitle"
      />
      {showAutoplay && (
        <label className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-white/85 mt-0.5">
          <input
            type="checkbox"
            checked={autoplayValue}
            onChange={(e) => onAutoplayChange(e.target.checked)}
            data-testid="featured-hub-editor-autoplay"
            className="w-3.5 h-3.5 accent-emerald-500"
          />
          Autoplay video on load
        </label>
      )}
      <div className="flex gap-1.5 justify-end mt-1">
        <button type="button" onClick={onCancel} className="w-7 h-7 rounded bg-slate-700 text-white flex items-center justify-center" data-testid="featured-hub-editor-cancel">
          <X className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={onSave} className="w-7 h-7 rounded bg-emerald-600 text-white flex items-center justify-center" data-testid="featured-hub-editor-save">
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
